import { faClose, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Grid, IconButton, Stack, TextField } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import StyledDivider from 'components/general/StyledDivider';
import LabeledDateTimePicker from 'components/general/inputs/LabeledPickers/LabeledDateTimePicker';
import {
  IFixedSchedule,
  IRelativeSchedule,
  TRoutine,
  duration,
} from 'components/general/types/cdh';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import theme from 'theme';
import { mjd2Moment, moment2Mjd } from 'utils/time';
import { IRoutineFormProps } from '.';
import { RoutineContext, useRoutineState } from '../RoutinesWidget';
import { addBlockToState } from '../RoutinesWidget/utils';
import useStyles from './styles';

const RelativeInput = ({
  start,
  routine,
  index,
  value,
  setValue,
}: {
  start: boolean;
  routine: string;
  index: number;
  value: number;
  setValue: (value: unknown, key: string) => void;
}) => {
  const { classes } = useStyles();
  const days = Math.floor(value / (24 * 60));

  return (
    <div>
      <label>{start ? 'Start' : 'Stop'}</label>
      <TextField
        className={classes.timePickerInput}
        type="number"
        value={days}
        onChange={(e) => {
          const daysInMinutes = Number(e.target.value) * 24 * 60;
          const newTime = value - days * (24 * 60) + daysInMinutes;
          setValue(newTime, `scheduleData.${routine}.activeTimes.${index}.${start ? 0 : 1}.min`);
        }}
        InputProps={{ endAdornment: <h6>Days</h6> }}
      />
      <TimePicker
        ampm={false}
        slotProps={{
          textField: {
            fullWidth: true,
            className: classes.timePickerInput,
          },
          openPickerIcon: { style: { fontSize: 20 } },
          openPickerButton: { style: { width: 30, height: 30 } },
          popper: {
            container: document.getElementById('routines-diagram-container'),
          },
        }}
        timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
        views={['hours', 'minutes', 'seconds']}
        value={moment.utc('2024-1-1 00:00:00').add(value * 60, 'seconds')}
        onChange={(value) => {
          if (value) {
            const newTime =
              value.hours() * 60 + value.minutes() + value.seconds() / 60 + days * (24 * 60);
            setValue(newTime, `scheduleData.${routine}.activeTimes.${index}.${start ? 0 : 1}.min`);
          }
        }}
      />
    </div>
  );
};

const FixedInput = ({
  start,
  routine,
  index,
  value,
  setValue,
}: {
  start: boolean;
  routine: string;
  index: number;
  value: number;
  setValue: (value: unknown, key: string) => void;
}) => {
  return (
    <div>
      <LabeledDateTimePicker
        label={start ? 'Start' : 'Stop'}
        name={start ? 'start' : 'stop'}
        value={mjd2Moment(value)}
        onChange={(value) => {
          if (value) {
            const newTime = moment2Mjd(value);
            setValue(newTime, `scheduleData.${routine}.activeTimes.${index}.${start ? 0 : 1}`);
          }
        }}
        slotProps={{
          popper: {
            container: document.getElementById('routines-diagram-container'),
          },
        }}
      />
    </div>
  );
};

const ScheduleForm = ({ id }: IRoutineFormProps) => {
  const { state, setState } = useContext(RoutineContext);
  const { data: routine, setValue } = useRoutineState<IRelativeSchedule | IFixedSchedule>(id);

  const scheduleData = useMemo(() => {
    const data = Object.keys(routine.scheduleData).flatMap((id) =>
      routine.scheduleData[id].activeTimes.map(([start, stop], i) => ({
        routine: id,
        start,
        stop,
        index: i,
      }))
    );
    data.sort(({ start: aStart, stop: aStop }, { start: bStart, stop: bStop }) => {
      let a, b, a2, b2;
      a = b = a2 = b2 = 0;
      if (routine.type === 'RelativeSchedule') {
        a = (aStart as duration).min || 0;
        b = (bStart as duration).min || 0;
        a2 = (aStop as duration).min || 0;
        b2 = (bStop as duration).min || 0;
      } else {
        a = aStart as number;
        b = bStart as number;
        a2 = aStop as number;
        b2 = bStop as number;
      }
      return a - b || a2 - b2;
    });
    return data;
  }, [routine.scheduleData, routine.type]);

  return (
    <div>
      <h3>Schedule</h3>
      <div style={{ display: 'flex', width: 'max-content', paddingTop: 4 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '150px',
            borderRight: `1px solid ${theme.palette.background.contrastText}`,
            paddingRight: 4,
            textAlign: 'center',
            gap: 4,
          }}
        >
          <h6>Add to schedule</h6>
          {routine.subroutines.map((subroutineId) => {
            const subroutine = state.blocks[subroutineId] as TRoutine;
            const isDefault = routine.defaultSubroutine === subroutine.id;
            return (
              <div
                key={subroutineId}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <h6>{subroutine.name}</h6>
                <div style={{ display: 'inline-flex', gap: 4 }}>
                  <IconButton
                    style={{
                      width: 24,
                      height: 24,
                      fontSize: 12,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.contrastText,
                    }}
                    onClick={() => {
                      setState((prev) => {
                        const newState = {
                          blocks: { ...prev.blocks },
                          dirtyBlocks: [...prev.dirtyBlocks],
                          deletedBlocks: prev.deletedBlocks,
                        };
                        if (routine.type === 'FixedSchedule') {
                          const routineData = { ...newState.blocks[id] } as IFixedSchedule;
                          const newTimes: [moment.Moment | number, moment.Moment | number][] = [
                            ...routineData.scheduleData[subroutineId].activeTimes,
                            [moment2Mjd(moment()), moment2Mjd(moment())],
                          ];
                          routineData.scheduleData = {
                            ...routineData.scheduleData,
                            [subroutineId]: { activeTimes: newTimes },
                          };
                          addBlockToState(routineData, newState);
                        } else {
                          const routineData = { ...newState.blocks[id] } as IRelativeSchedule;
                          const newTimes: [duration, duration][] = [
                            ...routineData.scheduleData[subroutineId].activeTimes,
                            [{ min: 0 }, { min: 0 }],
                          ];
                          routineData.scheduleData = {
                            ...routineData.scheduleData,
                            [subroutineId]: { activeTimes: newTimes },
                          };
                          addBlockToState(routineData, newState);
                        }
                        return newState;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </IconButton>
                  <Chip
                    variant={isDefault ? 'filled' : 'outlined'}
                    color={isDefault ? 'primary' : 'default'}
                    label="default"
                    size="small"
                    onClick={() => {
                      setValue(isDefault ? null : subroutine.id, 'defaultSubroutine');
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <Stack
          direction="column"
          spacing={1}
          style={{
            padding: 4,
            width: '400px',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <StyledDivider style={{ margin: '0px 5px', width: '100%' }} />{' '}
            <p style={{ whiteSpace: 'nowrap', margin: 0 }}>Simulation Start</p>
            <StyledDivider style={{ margin: '0px 5px', width: '100%' }} />
          </div>
          {scheduleData.map((data) => {
            const subroutine = state.blocks[data.routine] as TRoutine;
            return (
              <Grid
                key={data.routine + data.index}
                style={{
                  width: '100%',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[5],
                  padding: '0px 10px 8px',
                  borderRadius: 4,
                  textAlign: 'center',
                  position: 'relative',
                }}
                container
                spacing={1}
                alignItems="center"
              >
                <IconButton
                  onClick={() => {
                    setState((prev) => {
                      const newState = {
                        blocks: { ...prev.blocks },
                        dirtyBlocks: [...prev.dirtyBlocks],
                        deletedBlocks: prev.deletedBlocks,
                      };
                      const scheduler = { ...newState.blocks[id] } as IRelativeSchedule;
                      const newTimes = [...scheduler.scheduleData[data.routine].activeTimes];
                      newTimes.splice(data.index, 1);
                      scheduler.scheduleData = {
                        ...scheduler.scheduleData,
                        [data.routine]: { activeTimes: newTimes },
                      };
                      addBlockToState(scheduler, newState);
                      return newState;
                    });
                  }}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.background.contrastText,
                    width: 20,
                    height: 20,
                    fontSize: 12,
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </IconButton>
                <Grid item xs={4}>
                  <h5 style={{ color: theme.palette.primary.light }}>{subroutine.name}</h5>
                </Grid>
                <Grid item xs={4}>
                  {routine.type === 'RelativeSchedule' ? (
                    <RelativeInput
                      start={true}
                      routine={data.routine}
                      index={data.index}
                      value={(data.start as duration).min || 0}
                      setValue={setValue}
                    />
                  ) : (
                    <FixedInput
                      start={true}
                      routine={data.routine}
                      index={data.index}
                      value={data.start as number}
                      setValue={setValue}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  {routine.type === 'RelativeSchedule' ? (
                    <RelativeInput
                      start={false}
                      routine={data.routine}
                      index={data.index}
                      value={(data.stop as duration).min || 0}
                      setValue={setValue}
                    />
                  ) : (
                    <FixedInput
                      start={false}
                      routine={data.routine}
                      index={data.index}
                      value={data.stop as number}
                      setValue={setValue}
                    />
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      </div>
    </div>
  );
};

export default ScheduleForm;
